import { Injectable, OnDestroy } from '@angular/core'
import { BulkClearwaterTransactionsKey } from '@helios/panels/bulk-clearwater-transactions'
import { BulkExternalTransactionsKey } from '@helios/panels/bulk-external-transactions'
import { CorporateActionsKey } from '@helios/panels/corporate-actions'
import { DistributionsKey } from '@helios/panels/distributions'
import { PositionsKey } from '@helios/panels/positions'
import { PricingKey } from '@helios/panels/pricing'
import { TransactionsKey } from '@helios/panels/transactions'
import {
  Break,
  BreakCategory,
  IssueGroup,
  RootState,
  breaksBulkEditAction,
  clearFocusedCWTransaction,
  clearFocusedExtTranId,
  clearFocusedPositionSecurity,
  focusPanelAction,
  setFocusedAssetId,
  setFocusedBreak,
  setFocusedBulkCwTransactions,
  setFocusedBulkExtTransactions,
  setFocusedCWTransaction,
  setFocusedCorporateActionIssue,
  setFocusedExtTransaction,
  setFocusedPositionSecurity,
  setFocusedSecurities,
  setFocusedSubSleeveAccountId,
  showBreakDetailsAction,
} from '@helios/state-management'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  getBreakAccountId,
  getBreakAssetId,
  getBreakExtTranId,
  getBreakPositionSecurityId,
  getBreakSecurityId,
  getBreakTransactionId,
  getBreaksCwTransactionIds,
  getBreaksExtTransactionIds,
} from './breaks-metadata'

/**
 * Listen to redux for focused breaks and trigger the FocusPanel Redux action to focus the appropriate panel on the appropriate item.
 * Depending on the details of the particular break, this determines what item needs to be focused on and sets that item as focused in Redux.
 *   - for example, a transaction break should find the tranId in the break metadata, and set that transaction as focused.
 * The focused panel is expected to monitor for the focused item and react accordingly.
 * Other panels may watch for the same focused item, but only one will be auto-focused for that particular break type.
 */
@Injectable()
export class BreakFocusEffects implements OnDestroy {
  private readonly _destroyed$ = new Subject<void>()

  constructor(
    private readonly store: Store<RootState>,
    private readonly actions$: Actions,
  ) {
    //single click, or select, on a break
    this.actions$ //note: normally, we'd use store.select, but that doesn't work here, possibly due to this being an effect
      .pipe(takeUntil(this._destroyed$), ofType(setFocusedBreak))
      .subscribe(action => this.onBreakFocused(action['payload'], false))

    //"show details" or double-click - just an event
    this.actions$
      .pipe(takeUntil(this._destroyed$), ofType(showBreakDetailsAction))
      .subscribe(action => this.onBreakFocused(action['payload'], true))

    //"bulk edit"
    this.actions$
      .pipe(takeUntil(this._destroyed$), ofType(breaksBulkEditAction))
      .subscribe(action =>
        this.onBreaksBulkEditFocused(action['payload'], true),
      )
  }

  ngOnDestroy(): void {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  /**
   * demux break handlers by category
   */
  private onBreakFocused(
    issue: Break | undefined,
    isShowDetailsAction: boolean,
  ) {
    if (!issue) return

    //Open transaction panel if Transaction id!=null
    if (getBreakTransactionId(issue)) {
      this.onTransactionBreak(issue, isShowDetailsAction)
    } else {
      switch (issue.categoryName) {
        case BreakCategory.CORPORATE_ACTIONS:
          this.onCorporateActionBreak(issue, isShowDetailsAction)
          break
        case BreakCategory.DISTRIBUTION:
          this.onDistributionBreak(issue, isShowDetailsAction)
          break
        case BreakCategory.POSITION:
          this.onPositionBreak(issue, isShowDetailsAction)
          break
        case BreakCategory.PRICE_AND_ANALYTICS:
          this.onPricingBreak(issue, isShowDetailsAction)
          if (issue.displayName == IssueGroup.TRADE_PRICE_VARIANCE)
            this.onTransactionBreak(issue, isShowDetailsAction)
          break
        case BreakCategory.TRANSACTION_BREAKS:
        case BreakCategory.TRANSACTION_DATA_INTEGRITY:
          this.onTransactionBreak(issue, isShowDetailsAction)
          break
        default:
          console.warn(`unhandled break category: ${issue.categoryName}`)
      }
    }
  }

  private onBreaksBulkEditFocused(
    issues: Break[] | undefined,
    isBreaksBulkEditAction: boolean,
  ) {
    if (!issues) return

    switch (issues[0].categoryName) {
      case BreakCategory.TRANSACTION_BREAKS:
      case BreakCategory.TRANSACTION_DATA_INTEGRITY:
        this.onBulkEditTransactionBreak(issues, isBreaksBulkEditAction)
        break
    }
  }

  private onCorporateActionBreak(issue: Break, isShowDetailsAction: boolean) {
    this.store.dispatch(setFocusedCorporateActionIssue(issue.id))

    if (isShowDetailsAction) this.focusPanel(CorporateActionsKey)
  }

  private onDistributionBreak(issue: Break, isShowDetailsAction: boolean) {
    const focusedSecId = getBreakSecurityId(issue)
    const focusedAssetId = getBreakAssetId(issue)
    this.setFocusedSecurity(focusedSecId)
    this.store.dispatch(setFocusedAssetId(focusedAssetId))
    this.store.dispatch(setFocusedCWTransaction())
    if (isShowDetailsAction) this.focusPanel(DistributionsKey)
  }

  private onPositionBreak(issue: Break, isShowDetailsAction: boolean) {
    const positionSecurityId = getBreakPositionSecurityId(issue)
    this.store.dispatch(setFocusedPositionSecurity(positionSecurityId))

    if (isShowDetailsAction) this.focusPanel(PositionsKey)

    // Clear because panel received the focused item
    setTimeout(() => this.store.dispatch(clearFocusedPositionSecurity()), 10)
  }

  private onPricingBreak(issue: Break, isShowDetailsAction: boolean) {
    const focusedSecId = getBreakSecurityId(issue)
    const focusedAccId = getBreakAccountId(issue)
    this.setFocusedSecurity(focusedSecId)
    if (focusedAccId)
      this.store.dispatch(
        setFocusedSubSleeveAccountId({
          subSleeveAccountId: focusedAccId,
        }),
      )

    if (isShowDetailsAction) this.focusPanel(PricingKey)
  }

  private onTransactionBreak(issue: Break, isShowDetailsAction: boolean) {
    const focusedTranId = getBreakTransactionId(issue)
    const focusedExtTranId = getBreakExtTranId(issue)
    this.store.dispatch(setFocusedAssetId(undefined))
    this.store.dispatch(setFocusedCWTransaction(focusedTranId))
    this.store.dispatch(setFocusedExtTransaction(focusedExtTranId))

    if (isShowDetailsAction) this.focusPanel(TransactionsKey)

    // Clear because panel received the focused item
    setTimeout(() => {
      this.store.dispatch(clearFocusedCWTransaction())
      this.store.dispatch(clearFocusedExtTranId())
    }, 10)
  }

  private onBulkEditTransactionBreak(
    issues: Break[] | undefined,
    isBreaksBulkEditAction: boolean,
  ) {
    if (!issues) return

    const focusedTranIds = getBreaksCwTransactionIds(issues)
    const focusedExtTranIds = getBreaksExtTransactionIds(issues)

    if (issues[0].displayName === 'Unentered Transactions') {
      this.store.dispatch(
        setFocusedBulkExtTransactions({
          tranIds: focusedExtTranIds ? focusedExtTranIds : [],
        }),
      )
      if (isBreaksBulkEditAction) this.focusPanel(BulkExternalTransactionsKey)
    } else {
      this.store.dispatch(
        setFocusedBulkCwTransactions({
          tranIds: focusedTranIds ? focusedTranIds : [],
        }),
      )
      if (isBreaksBulkEditAction) this.focusPanel(BulkClearwaterTransactionsKey)
    }
  }

  private setFocusedSecurity(securityId: number | undefined) {
    this.store.dispatch(
      setFocusedSecurities({ securityIds: securityId ? [securityId] : [] }),
    )
  }

  private focusPanel(panelKey: string) {
    this.store.dispatch(focusPanelAction(panelKey))
  }
}
